import socketService from './SocketService';
import httpClient from '../httpClient';

// ===== Types =====

export interface ChatMessage {
  messageId: string;
  conversationId: string;
  sender: {
    uuid: string;
    email: string;
    role_id: number;
  };
  senderType: 'user' | 'agent';
  messageType: 'text' | 'file' | 'image' | 'system';
  content?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  isRead: boolean;
  timestamp: Date;
}

export interface Conversation {
  conversation_id: string;
  status: 'waiting' | 'active' | 'closed';
  started_at: Date;
  last_message_at: Date;
  closed_at?: Date;
  rating?: number;
  agent_email?: string;
  unread_count: number;
  last_message?: string;
}

export interface ChatRequestResponse {
  success: boolean;
  conversationId?: string;
  status?: string;
  message?: string;
}

export interface SendMessageResponse {
  success: boolean;
  messageId?: string;
  message?: string;
}

export interface UploadFileResponse {
  success: boolean;
  messageId?: string;
  fileUrl?: string;
  message?: string;
}

// ===== Chat Service =====

class ChatService {
  /**
   * Request a new chat session
   * @returns Promise with conversation ID and status
   */
  requestChat(): Promise<ChatRequestResponse> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      socketService.emit('chat:request', (response: ChatRequestResponse) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.message || 'Failed to request chat'));
        }
      });
    });
  }

  /**
   * Send a text message in a conversation
   * @param conversationId - Conversation ID
   * @param content - Message content
   * @returns Promise with message ID
   */
  sendMessage(conversationId: string, content: string): Promise<SendMessageResponse> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      socketService.emit(
        'chat:send_message',
        { conversationId, content },
        (response: SendMessageResponse) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Failed to send message'));
          }
        }
      );
    });
  }

  /**
   * Send typing indicator
   * @param conversationId - Conversation ID
   * @param isTyping - Whether user is typing
   */
  sendTypingIndicator(conversationId: string, isTyping: boolean): void {
    if (!socketService.isConnected()) return;

    socketService.emit('chat:typing', { conversationId, isTyping });
  }

  /**
   * Upload a file (image/document) in a conversation
   * @param conversationId - Conversation ID
   * @param file - File to upload
   * @returns Promise with file URL
   */
  async uploadFile(conversationId: string, file: File): Promise<UploadFileResponse> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        reject(new Error('File size exceeds 10MB limit'));
        return;
      }

      // Validate file type
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
      ];

      if (!allowedTypes.includes(file.type)) {
        reject(new Error('File type not allowed'));
        return;
      }

      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const base64Data = reader.result as string;

        socketService.emit(
          'chat:upload_file',
          {
            conversationId,
            file: base64Data,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          },
          (response: UploadFileResponse) => {
            if (response.success) {
              resolve(response);
            } else {
              reject(new Error(response.message || 'Failed to upload file'));
            }
          }
        );
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
    });
  }

  /**
   * Mark messages as read
   * @param conversationId - Conversation ID
   * @param messageIds - Array of message IDs to mark as read
   */
  markMessagesAsRead(conversationId: string, messageIds: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      socketService.emit(
        'chat:mark_read',
        { conversationId, messageIds },
        (response: { success: boolean; message?: string }) => {
          if (response.success) {
            resolve();
          } else {
            reject(new Error(response.message || 'Failed to mark messages as read'));
          }
        }
      );
    });
  }

  /**
   * Close a conversation
   * @param conversationId - Conversation ID
   * @param rating - Optional rating (1-5)
   * @param feedback - Optional feedback text
   */
  closeConversation(
    conversationId: string,
    rating?: number,
    feedback?: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      socketService.emit(
        'chat:close',
        { conversationId, rating, feedback },
        (response: { success: boolean; message?: string }) => {
          if (response.success) {
            resolve();
          } else {
            reject(new Error(response.message || 'Failed to close conversation'));
          }
        }
      );
    });
  }

  /**
   * Get chat history for a conversation
   * @param conversationId - Conversation ID
   * @param limit - Number of messages to fetch
   * @param offset - Offset for pagination
   */
  getChatHistory(
    conversationId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ChatMessage[]> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      socketService.emit(
        'chat:get_history',
        { conversationId, limit, offset },
        (response: { success: boolean; messages?: ChatMessage[]; message?: string }) => {
          if (response.success && response.messages) {
            resolve(response.messages);
          } else {
            reject(new Error(response.message || 'Failed to get chat history'));
          }
        }
      );
    });
  }

  /**
   * Listen for incoming messages
   * @param callback - Callback to handle new messages
   */
  onMessage(callback: (message: ChatMessage) => void): void {
    socketService.on('chat:message', callback);
  }

  /**
   * Stop listening for incoming messages
   */
  offMessage(callback?: (message: ChatMessage) => void): void {
    socketService.off('chat:message', callback);
  }

  /**
   * Listen for typing indicators
   * @param callback - Callback to handle typing events
   */
  onTypingIndicator(
    callback: (data: {
      conversationId: string;
      user: { uuid: string; email: string };
      isTyping: boolean;
    }) => void
  ): void {
    socketService.on('chat:typing_indicator', callback);
  }

  /**
   * Stop listening for typing indicators
   */
  offTypingIndicator(): void {
    socketService.off('chat:typing_indicator');
  }

  /**
   * Listen for agent joined event
   * @param callback - Callback to handle agent join
   */
  onAgentJoined(
    callback: (data: {
      conversationId: string;
      agent: { uuid: string; email: string };
      timestamp: Date;
    }) => void
  ): void {
    socketService.on('chat:agent_joined', callback);
  }

  /**
   * Stop listening for agent joined events
   */
  offAgentJoined(): void {
    socketService.off('chat:agent_joined');
  }

  /**
   * Listen for conversation closed event
   * @param callback - Callback to handle conversation close
   */
  onChatClosed(
    callback: (data: {
      conversationId: string;
      closedBy: { uuid: string; email: string };
      timestamp: Date;
    }) => void
  ): void {
    socketService.on('chat:closed', callback);
  }

  /**
   * Stop listening for conversation closed events
   */
  offChatClosed(): void {
    socketService.off('chat:closed');
  }

  // ===== REST API Methods =====

  /**
   * Get user's conversations via REST API
   * @param status - Optional status filter
   */
  async getUserConversations(status?: string): Promise<Conversation[]> {
    try {
      const url = status ? `/support/conversations?status=${status}` : '/support/conversations';
      const response = await httpClient.get<{ success: boolean; conversations: Conversation[] }>(url);
      return response.conversations || [];
    } catch (error: any) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  /**
   * Get messages for a conversation via REST API
   * @param conversationId - Conversation ID
   * @param limit - Number of messages
   * @param offset - Offset for pagination
   */
  async getConversationMessages(
    conversationId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ChatMessage[]> {
    try {
      const response = await httpClient.get<{ success: boolean; messages: ChatMessage[] }>(
        `/support/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`
      );
      return response.messages || [];
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const chatService = new ChatService();
export default chatService;
